import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["audio", "playButton", "playIcon", "stationName", "logo", "volumeSlider"]

    connect() {
        console.log("📻 Radio Player Controller connected!")
        console.log("🎯 Targets:", {
            audio: this.hasAudioTarget,
            playButton: this.hasPlayButtonTarget,
            playIcon: this.hasPlayIconTarget,
            stationName: this.hasStationNameTarget,
            logo: this.hasLogoTarget,
            volumeSlider: this.hasVolumeSliderTarget
        })

        // Configura o áudio
        this.audioTarget.crossOrigin = "anonymous"
        this.audioTarget.playsInline = true
        this.audioTarget.preload = "none"
        this.isLoading = false

        // Carrega volume salvo
        const savedVolume = localStorage.getItem("radioVolume") || 80
        this.audioTarget.volume = savedVolume / 100
        if (this.hasVolumeSliderTarget) {
            this.volumeSliderTarget.value = savedVolume
        }

        // Event listeners do áudio
        this.audioTarget.addEventListener("play", () => this.updateButton(true))
        this.audioTarget.addEventListener("pause", () => {
            this.setLoading(false)
            this.updateButton(false)
        })
        this.audioTarget.addEventListener("error", (e) => this.handleError(e))
        this.audioTarget.addEventListener("playing", () => console.log("🎵 Audio is playing"))
        this.audioTarget.addEventListener("loadstart", () => this.setLoading(true))
        this.audioTarget.addEventListener("waiting", () => {
            console.log("⏳ Audio buffering...")
            this.setLoading(true)
        })
        this.audioTarget.addEventListener("canplay", () => {
            console.log("✅ Audio ready to play")
            this.setLoading(false)
        })
        this.audioTarget.addEventListener("playing", () => this.setLoading(false))

        if (this.audioTarget.getAttribute("src")) {
            this.showPlayer()
        } else {
            this.hidePlayer()
        }

        this.updateButton(!this.audioTarget.paused)

        this.boundHandleRadioPlay = this.handleRadioPlay.bind(this)
        window.addEventListener("radio:play", this.boundHandleRadioPlay)
    }

    playbackDetail() {
        const attr = this.audioTarget.getAttribute("src")
        if (!attr) {
            return { streamUrl: "", playing: false }
        }
        let streamUrl = ""
        try {
            streamUrl = new URL(attr, window.location.href).href
        } catch {
            streamUrl = attr
        }
        return { streamUrl, playing: !this.audioTarget.paused }
    }

    notifyStationButtons() {
        window.dispatchEvent(
            new CustomEvent("radio:playback", { detail: this.playbackDetail() })
        )
    }

    notifyLoading(url, loading) {
        window.dispatchEvent(
            new CustomEvent("radio:loading", { detail: { url, loading } })
        )
    }

    disconnect() {
        console.log("📻 Radio Player Controller disconnected")
        if (this.hasVolumeSliderTarget) {
            localStorage.setItem("radioVolume", this.volumeSliderTarget.value)
        }
        if (this.boundHandleRadioPlay) {
            window.removeEventListener("radio:play", this.boundHandleRadioPlay)
        }
    }

    togglePlay(event) {
        event.preventDefault()
        console.log("🔘 Toggle play clicked")

        if (this.audioTarget.paused) {
            this.play()
        } else {
            this.pause()
        }
    }

    playStation(event) {
        event.preventDefault()

        // Debug: verifica o que está chegando
        console.log("🔍 Event object:", event)
        console.log("🔍 Event params:", event.params)
        console.log("🔍 Event detail:", event.detail)

        // Tenta pegar os params de diferentes formas
        const url = event.params?.url || event.target.dataset.radioPlayerUrlParam
        const name = event.params?.name || event.target.dataset.radioPlayerNameParam
        const logo = event.params?.logo || event.target.dataset.radioPlayerLogoParam

        console.log("🎵 playStation called:", { url, name, logo })

        if (!url) {
            console.error("❌ No URL provided!")
            console.log("📋 Available dataset:", event.target.dataset)
            alert("Erro: URL da rádio não disponível")
            return
        }

        // Atualiza informações da estação no player
        if (this.hasStationNameTarget) {
            this.stationNameTarget.textContent = name || "Rádio desconhecida"
        }

        if (this.hasLogoTarget) {
            if (logo) {
                this.logoTarget.innerHTML = `<img src="${logo}" alt="${name}" class="h-12 w-12 rounded object-cover">`
            } else {
                this.logoTarget.innerHTML = `<span class="text-xl font-bold">${name?.charAt(0) || 'R'}</span>`
            }
        }

        // Para áudio atual e limpa source
        this.pause()

        // Configura novo áudio
        this.audioTarget.src = url
        this.audioTarget.load()

        console.log("🔊 Loading audio from:", url)

        // Toca após pequeno delay para garantir que load() foi processado
        setTimeout(() => this.play(), 200)
    }

    handleRadioPlay(event) {
        const { url, name, logo } = event.detail
        this.playStationDirect(url, name, logo)
    }

    playStationDirect(url, name, logo) {
        if (!url) {
            console.error("❌ No URL provided!")
            return
        }

        console.log("🎵 Playing station:", { url, name, logo })
        this.showPlayer()
        if (this.hasStationNameTarget) {
            this.stationNameTarget.textContent = name || "Rádio desconhecida"
        }

        if (this.hasLogoTarget) {
            if (logo) {
                this.logoTarget.innerHTML = `<img src="${logo}" alt="${name}" class="h-15 w-15 rounded object-cover">`
            } else {
                this.logoTarget.innerHTML = `<span class="text-xl font-bold">${name?.charAt(0) || 'R'}</span>`
            }
        }

        this.pause()
        this.setLoading(true)
        this.notifyLoading(url, true)
        this.audioTarget.src = url
        this.audioTarget.load()

        setTimeout(() => this.play(), 200)
    }

    play() {
        console.log("▶️ Attempting to play:", this.audioTarget.src)
        this.showPlayer()
        this.setLoading(true)
        this.notifyLoading(this.audioTarget.src, true)

        const playPromise = this.audioTarget.play()

        if (playPromise !== undefined) {
            playPromise
                .then(() => {
                    console.log("✅ Playback started successfully")
                    this.setLoading(false)
                })
                .catch((error) => {
                    console.error("❌ Playback failed:", error.name, error.message)
                    if (this.hasStationNameTarget) {
                        this.stationNameTarget.textContent = "Erro - clique para tentar novamente"
                    }
                    this.setLoading(false)
                    this.notifyStationButtons()
                })
        }
    }

    pause() {
        this.audioTarget.pause()
        this.setLoading(false)
        this.notifyLoading(this.audioTarget.src, false)
        console.log("⏸️ Audio paused")
    }

    setVolume(event) {
        const volume = event.target.value
        this.audioTarget.volume = volume / 100
        localStorage.setItem("radioVolume", volume)
    }

    updateButton(playing) {
        if (!this.hasPlayIconTarget || !this.hasPlayButtonTarget) return

        if (this.isLoading) {
            this.playIconTarget.innerHTML = `<span class="loading loading-spinner loading-sm"></span>`
            this.playButtonTarget.classList.add("btn-secondary")
            this.playButtonTarget.classList.remove("btn-primary")
            this.notifyStationButtons()
            return
        }

        if (playing) {
            // Ícone de pause
            this.playIconTarget.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="14" y="3" width="5" height="18" rx="1"/><rect x="5" y="3" width="5" height="18" rx="1"/></svg>`
            this.playButtonTarget.classList.add("btn-secondary")
            this.playButtonTarget.classList.remove("btn-primary")
        } else {
            // Ícone de play
            this.playIconTarget.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 5a2 2 0 0 1 3.008-1.728l11.997 6.998a2 2 0 0 1 .003 3.458l-12 7A2 2 0 0 1 5 19z"/></svg>`
            this.playButtonTarget.classList.remove("btn-secondary")
            this.playButtonTarget.classList.add("btn-primary")
        }

        this.notifyStationButtons()
    }

    handleError(event) {
        const audio = event.target
        console.error("❌ Audio error:", {
            error: audio.error,
            code: audio.error?.code,
            message: audio.error?.message,
            src: audio.src,
            networkState: audio.networkState,
            readyState: audio.readyState
        })

        if (this.hasStationNameTarget) {
            this.stationNameTarget.textContent = "Erro ao carregar - CORS?"
        }
        this.setLoading(false)
        this.notifyLoading(audio.src, false)
        this.notifyStationButtons()
    }

    setLoading(loading) {
        const nextLoading = Boolean(loading)
        if (this.isLoading === nextLoading) return

        this.isLoading = nextLoading
        this.notifyLoading(this.audioTarget.src, nextLoading)
        this.updateButton(!this.audioTarget.paused)
    }

    showPlayer() {
        this.element.classList.remove("hidden")
    }

    hidePlayer() {
        this.element.classList.add("hidden")
    }
}
